import { getAnalytics, isSupported, logEvent, setUserProperties } from 'firebase/analytics';

import { initializeFirebaseWeb } from '@/adapters/pushNotification/firebaseInitialization.web';
import { sentryLogger } from '@/adapters/logger/sentryLogger';

import type { AnalyticsAdapter } from './types';

const createFirebaseAnalyticsAdapter = (): AnalyticsAdapter => {
  const state: {
    supported: boolean | null;
    analytics: ReturnType<typeof getAnalytics> | null;
  } = { supported: null, analytics: null };

  const ensureAnalytics = async () => {
    if (state.supported !== null) {
      return;
    }

    state.supported = await isSupported();
    if (!state.supported) {
      return;
    }

    state.analytics = getAnalytics(initializeFirebaseWeb());
  };

  return {
    track: (event, params) => {
      void ensureAnalytics()
        .then(() => {
          if (state.analytics) {
            logEvent(state.analytics, event, params);
          }
        })
        .catch((error) => {
          sentryLogger.captureException(error);
        });
    },
    setUserProperties: (properties) => {
      void ensureAnalytics()
        .then(() => {
          if (state.analytics) {
            const normalizedProperties: Record<string, string | null> = {};
            for (const [key, value] of Object.entries(properties)) {
              normalizedProperties[key] = value ?? null;
            }
            setUserProperties(state.analytics, normalizedProperties);
          }
        })
        .catch((error) => {
          sentryLogger.captureException(error);
        });
    },
  };
};

const firebaseAnalyticsAdapter = createFirebaseAnalyticsAdapter();

export { firebaseAnalyticsAdapter };
